import Axios from 'axios';
import config from '../../appsettings.json';

/**
 * The marketing site's own lead capture (brochure + prices). Posts to the
 * public lead web form (api/Sales/WebForm/{token}), so the visitor lands as a
 * Lead in the platform tenant's Sales pipeline, tagged with the lead source
 * the token belongs to. The token is public by design; the honeypot and the
 * API's "public" rate limit are the anti-abuse.
 */
export interface ISiteLeadSubmission {
    firstName: string;
    lastName: string;
    email: string;
    companyName?: string;
    message?: string;
    /** Honeypot — always empty from a real visitor. */
    hp?: string;
    gclid?: string;
    fbclid?: string;
    msclkid?: string;
    gbraid?: string;
    wbraid?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    landingPageUrl?: string;
    referrer?: string;
}

/** The token configured for the site (Marketing:BrochureFormToken); empty when not set up. */
export const siteLeadFormToken = (): string =>
    (typeof window !== 'undefined' && window.__APP_CONFIG__?.siteLeadFormToken) || '';

/** Click ids and UTMs of the current visit, for ad attribution on the Lead. */
export const visitAttribution = (): Partial<ISiteLeadSubmission> => {
    if (typeof window === 'undefined') return {};
    const p = new URLSearchParams(window.location.search);
    const get = (key: string) => p.get(key) ?? undefined;
    return {
        gclid: get('gclid'), fbclid: get('fbclid'), msclkid: get('msclkid'), gbraid: get('gbraid'), wbraid: get('wbraid'),
        utmSource: get('utm_source'), utmMedium: get('utm_medium'), utmCampaign: get('utm_campaign'),
        utmTerm: get('utm_term'), utmContent: get('utm_content'),
        landingPageUrl: window.location.href.slice(0, 1000),
        referrer: document.referrer ? document.referrer.slice(0, 1000) : undefined,
    };
};

/** True when the API stored the lead (the endpoint answers 200 with the outcome in the body). */
export async function submitSiteLead(token: string, body: ISiteLeadSubmission): Promise<boolean> {
    try {
        const resp = await Axios.post(`${config.ApiSettings.EndPoint}api/Sales/WebForm/${encodeURIComponent(token)}`, body, { withCredentials: false });
        const data = resp.data as { result?: number; payload?: { accepted?: boolean } } | undefined;
        return (data?.result === 200 || data?.result === 201) && data?.payload?.accepted !== false;
    } catch {
        return false;
    }
}
